import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CalendarClock, CalendarDays, ListChecks, NotebookPen, Video } from "lucide-react";
import { defineModuleUi, from, unwrap, useOrg } from "@vixio/data";
import { addDaysIso, isoDateIn, todayIso, zonedIso, lazyPage } from "@vixio/ui";
import "./api";
import { shortDay, type BookingView } from "./api";
import { ClientMeetingsHome, ClientMeetingsPage } from "./ClientPages";
import { VideoRoomPage } from "./VideoRoom";

export * from "./api";
export { VideoRoom, roomUrl } from "./VideoRoom";

// Telas carregadas sob demanda: cada uma vira um arquivo à parte no build.
const BookingsPage = lazyPage(() => import("./BookingsPage"), "BookingsPage");
const MeetingPage = lazyPage(() => import("./MeetingsPages"), "MeetingPage");
const MeetingsPage = lazyPage(() => import("./MeetingsPages"), "MeetingsPage");
const TasksPage = lazyPage(() => import("./MeetingsPages"), "TasksPage");
const AgendaSettingsPage = lazyPage(() => import("./SettingsPage"), "AgendaSettingsPage");

function NextBookingsWidget() {
  const org = useOrg();
  const today = todayIso();
  const q = useQuery({
    queryKey: ["agenda", "widget", org.id, today],
    queryFn: () =>
      unwrap<BookingView[]>(
        from("agenda", "v_bookings").select("*").eq("org_id", org.id).eq("status", "confirmed").gte("starts_at", new Date().toISOString())
          .lt("starts_at", zonedIso(addDaysIso(today, 3), "00:00")).order("starts_at").limit(6),
      ),
  });
  if (!q.data?.length) return <p className="muted">Nada marcado até depois de amanhã. <Link to="/relacionamento/agenda">Abrir agenda</Link></p>;
  return (
    <ul className="vx-stack" style={{ gap: 8, margin: 0, padding: 0, listStyle: "none" }}>
      {q.data.map((b) => (
        <li key={b.id} className="vx-row" style={{ flexWrap: "nowrap", alignItems: "baseline" }}>
          <span className="num text-2" style={{ width: 124, flexShrink: 0, whiteSpace: "nowrap" }}>
            {isoDateIn(b.starts_at, b.timezone) === today ? "hoje" : shortDay(isoDateIn(b.starts_at, b.timezone))} {b.local_time}
          </span>
          <span style={{ minWidth: 0 }}>
            {b.person_id ? <Link to={`/pessoas/${b.person_id}`}>{b.person_name}</Link> : b.person_name}
            <span className="text-2"> · {b.type_title}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

export default defineModuleUi({
  key: "agenda",
  title: "Agenda, reuniões e atas",
  domain: "relationship",
  icon: CalendarDays,
  menu: [
    { path: "/relacionamento/agenda", label: "Agenda", minRole: "member", icon: CalendarDays, order: 20, keywords: "reserva horário conversa 20 minutos remarcar cancelar" },
    { path: "/relacionamento/reunioes", label: "Reuniões e atas", minRole: "member", icon: NotebookPen, order: 30, keywords: "pauta ata decisões participantes" },
    { path: "/relacionamento/tarefas", label: "Tarefas", minRole: "member", icon: ListChecks, order: 35, keywords: "encaminhamentos pendências" },
    { path: "/relacionamento/horarios", label: "Horários e tipos", minRole: "manager", icon: CalendarClock, order: 38, keywords: "disponibilidade jornada bloqueio tipo de compromisso agenda pública" },
  ],
  routes: [
    { path: "/relacionamento/agenda", element: <BookingsPage />, minRole: "member" },
    { path: "/relacionamento/horarios", element: <AgendaSettingsPage />, minRole: "member" },
    { path: "/relacionamento/reunioes", element: <MeetingsPage />, minRole: "member" },
    { path: "/relacionamento/reunioes/:id", element: <MeetingPage />, minRole: "member" },
    { path: "/relacionamento/tarefas", element: <TasksPage />, minRole: "member" },
    {
      path: "/relacionamento/sala/:room",
      element: <VideoRoomPage back="/relacionamento/reunioes" crumbs={[{ label: "Reuniões", to: "/relacionamento/reunioes" }]} />,
      minRole: "member",
    },
  ],
  widgets: [{ key: "agenda.next", title: "Próximos compromissos", minRole: "member", span: 1, order: 5, element: NextBookingsWidget }],
  // Portal: reuniões do cliente (agendar pela agenda da equipe, entrar na sala de vídeo) e próximas reuniões no início.
  portal: {
    menu: [{ path: "/portal/reunioes", label: "Reuniões", minRole: "client", icon: Video, order: 30 }],
    routes: [
      { path: "/portal/reunioes", element: <ClientMeetingsPage />, minRole: "client" },
      {
        path: "/portal/reunioes/sala/:room",
        element: <VideoRoomPage back="/portal/reunioes" crumbs={[{ label: "Reuniões", to: "/portal/reunioes" }]} />,
        minRole: "client",
      },
    ],
    home: ClientMeetingsHome,
    homeOrder: 20,
  },
});
